import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserData } from '../auth/UserData';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleOAuth } from '../hooks/useGoogleOAuth';
import resolveUrlOrigin from '../utils/resolveUrlOrigin';
import SEO from '../components/SEO';
import AuthLayout from './landing/AuthLayout';
import { GoogleIcon, DiscordIcon } from './landing/icons';
import { setPendingPlan } from '../utils/pendingPlan';

const SignUp = () => {
  const { t } = useTranslation();
  const { user, isLoading, refreshUserData } = useUserData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlRefCode = searchParams.get('ref') || '';
  const urlPlan = searchParams.get('plan') || '';

  // "Start free trial" lands here with ?plan=<key>. Registration does not
  // start the trial by itself — Stripe checkout does — so remember the
  // intent across the login redirect. AppShell picks it up after login and
  // sends the user straight to checkout for that plan.
  useEffect(() => {
    setPendingPlan(urlPlan);
  }, [urlPlan]);

  // Typed/prefilled promo code. Creatives print a bare code ("USE CODE
  // MARTINSEN") with no link, so the field is the only attribution path for
  // anyone who did not arrive through /r/:code.
  const [referralCode, setReferralCode] = useState(urlRefCode);
  const [refStatus, setRefStatus] = useState('idle'); // idle | checking | valid | invalid
  const refCode = referralCode.trim().toLowerCase();

  const [formValues, setFormValues] = useState({ username: '', email: '', password: '', confirmPassword: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const discordPopupRef = useRef(null);
  const popupIntervalRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const backendUrl = window.__BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
  const backendOrigin = useMemo(() => resolveUrlOrigin(backendUrl), [backendUrl]);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Hvis allerede innlogget -> gå til appen
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/app', { replace: true });
    }
  }, [isLoading, user, navigate]);

  const clearPopupInterval = useCallback(() => {
    if (popupIntervalRef.current) {
      clearInterval(popupIntervalRef.current);
      popupIntervalRef.current = null;
    }
    if (discordPopupRef.current && !discordPopupRef.current.closed) {
      discordPopupRef.current.close();
    }
    discordPopupRef.current = null;
  }, []);

  useEffect(() => () => clearPopupInterval(), [clearPopupInterval]);

  // Debounced lookup so the user sees whether the code was accepted before
  // submitting. An unknown code never blocks signup — it just means the
  // account is not attributed to an affiliate.
  useEffect(() => {
    if (!refCode) {
      setRefStatus('idle');
      return undefined;
    }
    setRefStatus('checking');
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/affiliate/check-code/${encodeURIComponent(refCode)}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        const data = await res.json();
        setRefStatus(data && data.valid ? 'valid' : 'invalid');
      } catch (err) {
        // Network hiccup: stay silent rather than claiming the code is bad.
        if (err.name !== 'AbortError') setRefStatus('idle');
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [refCode]);

  useEffect(() => () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
  }, []);

  const handleMessage = useCallback(
    async (event) => {
      // Sikker origin-sjekk – aksepter kun meldinger fra backendOrigin dersom den kan resolves
      if (backendOrigin && event.origin !== backendOrigin) {
        return;
      }

      if (event.data === 'loggedIn') {
        clearPopupInterval();
        await refreshUserData();
        navigate('/app', { replace: true });
      } else if (event.data === 'authError') {
        clearPopupInterval();
        setErrorMessage(t('signup.errors.oauth'));
      }
    },
    [backendOrigin, clearPopupInterval, navigate, refreshUserData, t]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegistration = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!formValues.username || !formValues.email || !formValues.password || !formValues.confirmPassword) {
      setErrorMessage(t('signup.errors.missingFields'));
      return;
    }

    if (formValues.password !== formValues.confirmPassword) {
      setErrorMessage(t('signup.errors.passwordMismatch'));
      return;
    }

    if (formValues.password.length < 8) {
      setErrorMessage(t('signup.errors.passwordLength'));
      return;
    }

    if (!/[A-Z]/.test(formValues.password) || !/[0-9]/.test(formValues.password)) {
      setErrorMessage(t('signup.errors.passwordComplexity', 'Password must contain at least one uppercase letter and one number.'));
      return;
    }

    try {
      setSubmitting(true);

      if (!backendUrl) {
        setErrorMessage(t('common.backendUnavailable'));
        return;
      }

      const payload = {
        username: formValues.username,
        email: formValues.email,
        password: formValues.password,
      };
      if (refCode) payload.ref = refCode;

      const response = await axios.post(
        `${backendUrl}/auth/register`,
        payload,
        { withCredentials: true }
      );

      if (response.status === 201) {
        setSuccessMessage(t('signup.success.created'));
      } else {
        setSuccessMessage(t('signup.success.passwordCreated'));
      }

      setFormValues({ username: '', email: '', password: '', confirmPassword: '' });
      // Liten delay for å vise success før redirect
      redirectTimeoutRef.current = setTimeout(() => navigate('/login'), 1800);
    } catch (error) {
      if (error.response?.status === 409) {
        setErrorMessage(t('signup.errors.emailInUse'));
      } else if (error.response?.status === 400) {
        setErrorMessage(t('signup.errors.missingFields'));
      } else {
        setErrorMessage(t('signup.errors.server'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscordRegistration = () => {
    setErrorMessage('');
    clearPopupInterval();

    const width = 500;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    if (!backendUrl) {
      setErrorMessage(t('common.backendUnavailable'));
      return;
    }

    const popup = window.open(
      `${backendUrl}/auth/discord`,
      'Discord Registration',
      `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=yes,status=no`
    );

    if (!popup) {
      setErrorMessage(t('signup.errors.popupBlocked'));
      return;
    }

    discordPopupRef.current = popup;
    popupIntervalRef.current = setInterval(() => {
      if (!discordPopupRef.current || discordPopupRef.current.closed) {
        clearPopupInterval();
      }
    }, 500);
  };

  const handleGoogleSuccess = async (code) => {
    try {
      setSubmitting(true);
      setErrorMessage('');
      setSuccessMessage('');

      if (!backendUrl) {
        setErrorMessage(t('common.backendUnavailable'));
        return;
      }

      const googlePayload = { code };
      if (refCode) googlePayload.ref = refCode;
      await axios.post(`${backendUrl}/auth/google`, googlePayload, { withCredentials: true });
      await refreshUserData();
      navigate('/app', { replace: true });
    } catch (error) {
      console.error('Google registration error:', error);
      if (error.response?.status === 503 || error.response?.status === 502) {
        setErrorMessage(t('signup.errors.googleUnavailable'));
      } else {
        setErrorMessage(t('signup.errors.oauth'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleError = (err) => {
    console.error('Google OAuth error:', err);
    setErrorMessage(t('signup.errors.oauth'));
  };

  const { requestCode: requestGoogleCode } = useGoogleOAuth({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  const handleGoogleRegistration = () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (!googleClientId) {
      setErrorMessage(t('signup.errors.googleUnavailable'));
      return;
    }

    requestGoogleCode();
  };

  if (isLoading) {
    return (
      <div className="ftv2">
        <div className="auth-loading"><span className="spinner" style={{ color: 'var(--orange)' }} /></div>
      </div>
    );
  }

  return (
    <AuthLayout>
      <SEO
        title="Sign Up"
        description="Create your free FlockTrade account. Get access to trading signals, sentiment analysis, and a community of traders."
        path="/signup"
      />

      <h1 className="auth-title">{t('signup.title')}</h1>
      <p className="auth-sub">{t('signup.description')}</p>

      {errorMessage && (
        <div className="auth-alert error" role="alert" id="signup-error">{errorMessage}</div>
      )}
      {successMessage && (
        <div className="auth-alert success" role="status">{successMessage}</div>
      )}

      <form className="auth-form" onSubmit={handleRegistration} noValidate aria-label="Registrering">
        <div className="field">
          <label htmlFor="signup-username">{t('signup.usernameLabel')}</label>
          <input
            id="signup-username"
            name="username"
            value={formValues.username}
            onChange={handleInputChange}
            required
            autoComplete="username"
          />
        </div>
        <div className="field">
          <label htmlFor="signup-email">{t('signup.emailLabel')}</label>
          <input
            id="signup-email"
            name="email"
            type="email"
            value={formValues.email}
            onChange={handleInputChange}
            required
            autoComplete="email"
          />
        </div>
        <div className="field">
          <label htmlFor="signup-password">{t('signup.passwordLabel')}</label>
          <input
            id="signup-password"
            name="password"
            type="password"
            value={formValues.password}
            onChange={handleInputChange}
            required
            autoComplete="new-password"
          />
        </div>
        <div className="field">
          <label htmlFor="signup-confirm">{t('signup.confirmPasswordLabel')}</label>
          <input
            id="signup-confirm"
            name="confirmPassword"
            type="password"
            value={formValues.confirmPassword}
            onChange={handleInputChange}
            required
            autoComplete="new-password"
          />
        </div>
        <div className="field">
          <label htmlFor="signup-ref">{t('signup.referralLabel')}</label>
          <input
            id="signup-ref"
            name="referralCode"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck="false"
            maxLength={64}
            placeholder={t('signup.referralPlaceholder')}
            aria-describedby="signup-ref-hint"
          />
          <small
            id="signup-ref-hint"
            style={{
              fontSize: 12,
              color: refStatus === 'valid'
                ? 'var(--green)'
                : refStatus === 'invalid' ? 'var(--red)' : 'var(--text-dim)',
            }}
          >
            {refStatus === 'valid' && t('signup.referralValid')}
            {refStatus === 'invalid' && t('signup.referralInvalid')}
            {(refStatus === 'idle' || refStatus === 'checking') && t('signup.referralHint')}
          </small>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting} aria-busy={submitting}>
          {submitting ? <span className="spinner" aria-label="Registrerer..." /> : t('signup.submit')}
        </button>
      </form>

      <div className="auth-divider">{t('signup.orDivider')}</div>

      <div className="auth-oauth">
        <button type="button" className="btn btn-discord" onClick={handleDiscordRegistration}>
          <DiscordIcon />{t('signup.discordButton')}
        </button>
        <button type="button" className="btn btn-google" onClick={handleGoogleRegistration}>
          <GoogleIcon />{t('signup.googleButton')}
        </button>
      </div>

      <p className="auth-switch">
        {t('signup.haveAccount')}{' '}
        <RouterLink to="/login">{t('signup.loginLink')}</RouterLink>
      </p>
    </AuthLayout>
  );
};

export default SignUp;
